import { useCallback } from 'react';
import { Button } from '@carbon/react';
import { Image, Download } from '@carbon/react/icons';
import classNames from 'classnames';
import { useChartExportSection } from './chart-export-section';
import { useOpenExportImage } from './export-image-drawer';
import type { ChartImageExporter, OutputFormat } from './types';
import styles from './export-image-button.module.scss';

// Icon-only trigger anchoring: `header` (centred in a header bar), `overlay`
// (top-right, over the plot), `inline` (normal flow, for a host's own header row).
export type ExportButtonPlacement = 'header' | 'overlay' | 'inline';

interface ExportImageButtonProps {
  exporter: ChartImageExporter;
  chartLabel: string;
  filenamePrefix: string;
  // Omit (or pass `['png']`) for PNG-only and a hidden format selector.
  // Pass `['png', 'jpeg']` to surface the format radios in the drawer.
  supportedFormats?: OutputFormat[];
  className?: string;
  // When true, render a compact icon-only ghost button (download icon + hover
  // tooltip) instead of the labeled "Export Image" tertiary button. Used for
  // per-chart header triggers where space is tight. Both variants open the same
  // export drawer.
  iconOnly?: boolean;
  // Icon-only anchoring within the chart root. Ignored for the labeled variant.
  placement?: ExportButtonPlacement;
  // If provided, clicking calls this instead of opening the shared drawer atom
  // (used by self-contained hosts that render their own local-state push drawer).
  onOpen?: () => void;
}

export const ExportImageButton = ({
  exporter,
  chartLabel,
  filenamePrefix,
  supportedFormats,
  className,
  iconOnly = false,
  placement = 'header',
  onOpen,
}: ExportImageButtonProps) => {
  const openExport = useOpenExportImage();
  // Inside a ChartExportSection, open that section's drawer, next to the chart,
  // instead of the page's shared one.
  const openInSection = useChartExportSection();

  const handleClick = useCallback(() => {
    if (onOpen) {
      onOpen();
      return;
    }
    const payload = { exporter, chartLabel, filenamePrefix, supportedFormats };
    if (openInSection) {
      openInSection(payload);
    } else {
      openExport(payload);
    }
  }, [
    onOpen,
    openInSection,
    openExport,
    exporter,
    chartLabel,
    filenamePrefix,
    supportedFormats,
  ]);

  if (iconOnly) {
    const button = (
      <Button
        kind="ghost"
        size="sm"
        hasIconOnly
        renderIcon={Download}
        iconDescription="Export image"
        tooltipPosition="left"
        onClick={handleClick}
        className={className}
      />
    );
    // `inline`: normal flow, for a host that places it in its own header row.
    if (placement === 'inline') return button;
    // Otherwise position the wrapper span (not the inner Carbon button) so the
    // trigger anchors to the chart's positioned root. See the .module.scss.
    const anchorClass =
      placement === 'overlay' ? styles.anchorOverlay : styles.anchorHeader;
    return (
      <span className={classNames(styles.iconExportAnchor, anchorClass)}>
        {button}
      </span>
    );
  }

  return (
    <Button
      kind="tertiary"
      size="sm"
      renderIcon={Image}
      onClick={handleClick}
      className={className}
    >
      Export Image
    </Button>
  );
};
